import type { PricingBody, PricingGroup, PricingItem } from '@/types/pricing'

const KEY_RE = /^[a-z][a-z0-9-]*$/

/** 옵션 그룹의 한국어 이름 — 어드민 표와 변경 요약이 같은 문구를 써요. */
export const OPTION_GROUP_LABEL: Record<PricingGroup, string> = { feature: '기능', addon: '추가 옵션' }

/**
 * 저장 전 클라이언트 검증 — apps/api `PricingBody` 의 pydantic 규칙과 같은 내용이에요.
 * 서버도 같은 걸 다시 검증하니 여기 목적은 422 전에 사람이 읽을 메시지를 보여주는 것뿐이에요.
 */
export function validatePricingBody(body: PricingBody): string[] {
  const errors: string[] = []
  const groups: [string, PricingItem[]][] = [
    ['프로젝트 유형', body.project_types],
    ['옵션', body.options],
    ['월 지원', body.support_tiers],
  ]

  for (const [name, items] of groups) {
    for (const item of items) {
      if (!KEY_RE.test(item.key)) {
        errors.push(`${name}: 키 "${item.key}" 는 영소문자로 시작하고 영소문자·숫자·하이픈만 쓸 수 있어요.`)
      }
      if (item.label.trim() === '') errors.push(`${name}: "${item.key}" 의 라벨이 비어 있어요.`)
      if (!Number.isInteger(item.amount) || item.amount < 0) {
        errors.push(`${name}: "${item.key}" 의 금액은 0 이상 정수여야 해요.`)
      }
    }
    const keys = items.map((i) => i.key)
    const duplicated = [...new Set(keys.filter((key, i) => keys.indexOf(key) !== i))]
    if (duplicated.length > 0) errors.push(`${name}: 키가 중복됐어요 — ${duplicated.join(', ')}`)
  }

  if (body.project_types.length === 0) errors.push('프로젝트 유형은 최소 1개가 필요해요.')
  if (body.support_tiers.length === 0) errors.push('월 지원 항목은 최소 1개가 필요해요.')
  for (const type of body.project_types) {
    if (!Number.isInteger(type.page_amount) || type.page_amount < 0) {
      errors.push(`프로젝트 유형: "${type.key}" 의 페이지당 금액은 0 이상 정수여야 해요.`)
    }
  }
  const typeKeys = new Set(body.project_types.map((t) => t.key))
  for (const option of body.options) {
    const unknown = option.applies_to.filter((key) => !typeKeys.has(key))
    if (unknown.length > 0) {
      errors.push(`옵션: "${option.key}" 의 붙는 유형에 없는 키가 있어요 — ${unknown.join(', ')}`)
    }
  }

  // 프리셋은 다른 목록의 키를 가리키는 조합이라, 실제로 고를 수 있는지만 봐요. api 도 같은 규칙을 다시 검증해요.
  const supportKeys = new Set(body.support_tiers.map((t) => t.key))
  const optionByKey = new Map(body.options.map((o) => [o.key, o]))
  const presetKeys = body.presets.map((p) => p.key)
  const duplicatedPresets = [...new Set(presetKeys.filter((key, i) => presetKeys.indexOf(key) !== i))]
  if (duplicatedPresets.length > 0) errors.push(`프리셋: 키가 중복됐어요 — ${duplicatedPresets.join(', ')}`)
  for (const preset of body.presets) {
    const name = preset.label.trim() || preset.key
    if (!KEY_RE.test(preset.key)) {
      errors.push(`프리셋: 키 "${preset.key}" 는 영소문자로 시작하고 영소문자·숫자·하이픈만 쓸 수 있어요.`)
    }
    if (preset.label.trim() === '') errors.push(`프리셋: "${preset.key}" 의 라벨이 비어 있어요.`)
    if (!typeKeys.has(preset.type)) errors.push(`프리셋 '${name}': 없는 유형 키예요 — ${preset.type}`)
    if (!supportKeys.has(preset.support)) errors.push(`프리셋 '${name}': 없는 월 지원 키예요 — ${preset.support}`)
    if (!Number.isInteger(preset.pages) || preset.pages < 0 || preset.pages > body.max_pages) {
      errors.push(`프리셋 '${name}': 페이지 수는 0 이상 ${body.max_pages} 이하여야 해요.`)
    }
    for (const key of preset.options) {
      const option = optionByKey.get(key)
      if (!option) {
        errors.push(`프리셋 '${name}': 없는 옵션 키예요 — ${key}`)
        continue
      }
      if (option.applies_to.length > 0 && !option.applies_to.includes(preset.type)) {
        errors.push(`프리셋 '${name}': '${preset.type}' 유형에 안 붙는 옵션이에요 — ${key}`)
      }
    }
  }

  if (!Number.isInteger(body.max_pages) || body.max_pages < 1) errors.push('페이지 수 상한은 1 이상 정수여야 해요.')
  if (body.online_discount_rate < 0 || body.online_discount_rate > 0.9) errors.push('할인율은 0~90% 사이여야 해요.')
  if (!Number.isInteger(body.onetime_cap) || body.onetime_cap <= 0) errors.push('구축비 상한은 1원 이상 정수여야 해요.')
  if (
    !Number.isInteger(body.monthly_free_months) ||
    body.monthly_free_months < 0 ||
    body.monthly_free_months > 24
  ) {
    errors.push('월 유지보수 무료 개월은 0 이상 24 이하 정수여야 해요.')
  }
  return errors
}

/** index 항목을 delta 만큼 옮긴 새 배열. 범위를 벗어나면 원본을 그대로 돌려줘요(↑↓ 버튼용). */
export function moveItem<T>(items: T[], index: number, delta: number): T[] {
  const target = index + delta
  if (index < 0 || index >= items.length || target < 0 || target >= items.length) return items
  const next = [...items]
  const [moved] = next.splice(index, 1)
  next.splice(target, 0, moved)
  return next
}
